/**
 * Company Import Template — parse, validate, duplicate detection
 */

import { parseShareholdingPercent } from './shareholdingPercent';

export const COMPANY_TEMPLATE_COLUMNS = [
  { key: 'name', label: 'Company Name', required: true, example: 'Example Subsidiary LLC' },
  { key: 'tradeLicenseNumber', label: 'Trade License Number', required: true, example: 'TL-123456' },
  { key: 'structureType', label: 'Structure Type', required: true, example: 'Subsidiary', note: 'Required (Parent or Subsidiary)' },
  { key: 'governanceTypeCode', label: 'Company Classification', required: true, example: 'LS' },
  { key: 'country', label: 'Country', required: true, example: 'UAE' },
  { key: 'parentCompanyName', label: 'Parent Company Name', required: false, example: 'Etisalat Group PJSC', note: 'Optional (required for Subsidiary)' },
  { key: 'shareholdingPercentage', label: 'Shareholding %', required: false, example: '100' },
  { key: 'capital', label: 'Capital', required: false, example: 'AED 50M' },
  { key: 'entityClassification', label: 'Entity Classification', required: false, example: 'OPERATING' },
  { key: 'address', label: 'Address', required: false, example: 'Sheikh Zayed Road' },
  { key: 'city', label: 'City', required: false, example: 'Dubai' },
  { key: 'poBox', label: 'PO Box', required: false, example: '12345' },
  { key: 'phone', label: 'Phone', required: false, example: '+97141234567' },
  { key: 'email', label: 'Email', required: false, example: 'info@example.com' },
  { key: 'website', label: 'Website', required: false, example: 'https://example.com' },
  { key: 'foundedYear', label: 'Founded Year', required: false, example: '2010' },
  { key: 'employeeCount', label: 'Employees', required: false, example: '250' },
  { key: 'ceoName', label: 'CEO Name', required: false, example: 'Jane Doe' },
];

function escapeCsvCell(value) {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function generateCompanyTemplateData() {
  const headers = COMPANY_TEMPLATE_COLUMNS.map((col) => col.label);
  const examples = COMPANY_TEMPLATE_COLUMNS.map((col) => col.example);
  const notes = COMPANY_TEMPLATE_COLUMNS.map((col) => {
    if (col.note) return col.note;
    return col.required ? 'Required' : 'Optional';
  });

  return {
    headers,
    examples,
    notes,
    columns: COMPANY_TEMPLATE_COLUMNS,
  };
}

export function downloadCompanyTemplate() {
  const { headers, examples, notes } = generateCompanyTemplateData();
  const csvContent = [
    headers.map(escapeCsvCell).join(','),
    examples.map(escapeCsvCell).join(','),
    notes.map(escapeCsvCell).join(','),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `company_import_template_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function normalizeStructureType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'parent' || raw === 'group' || raw.includes('parent') || raw.includes('group')) {
    return 'Parent';
  }
  if (raw === 'subsidiary' || raw.includes('subsidiary')) {
    return 'Subsidiary';
  }
  return String(value).trim();
}

function safeString(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).trim();
}

function safeNumberString(value) {
  if (value === null || value === undefined || value === '') return '';
  return String(value).trim();
}

function getCell(row, label, key) {
  return row[label] ?? row[key];
}

export function parseImportedCompanyRow(row, index) {
  const parsed = {
    _rowIndex: index,
    name: safeString(getCell(row, 'Company Name', 'name')),
    tradeLicenseNumber: safeString(getCell(row, 'Trade License Number', 'tradeLicenseNumber')),
    structureType: normalizeStructureType(getCell(row, 'Structure Type', 'structureType')),
    governanceTypeCode: safeString(getCell(row, 'Company Classification', 'governanceTypeCode')),
    country: safeString(getCell(row, 'Country', 'country')),
    parentCompanyName: safeString(getCell(row, 'Parent Company Name', 'parentCompanyName')),
    shareholdingPercentage: safeNumberString(getCell(row, 'Shareholding %', 'shareholdingPercentage')),
    capital: safeString(getCell(row, 'Capital', 'capital')),
    entityClassification: safeString(getCell(row, 'Entity Classification', 'entityClassification')),
    address: safeString(getCell(row, 'Address', 'address')),
    city: safeString(getCell(row, 'City', 'city')),
    poBox: safeString(getCell(row, 'PO Box', 'poBox')),
    phone: safeString(getCell(row, 'Phone', 'phone')),
    email: safeString(getCell(row, 'Email', 'email')).toLowerCase(),
    website: safeString(getCell(row, 'Website', 'website')),
    foundedYear: safeNumberString(getCell(row, 'Founded Year', 'foundedYear')),
    employeeCount: safeNumberString(getCell(row, 'Employees', 'employeeCount')),
    ceoName: safeString(getCell(row, 'CEO Name', 'ceoName')),
  };

  return parsed;
}

export function isEmptyCompanyRow(row) {
  return !row.name
    && !row.tradeLicenseNumber
    && !row.structureType
    && !row.governanceTypeCode
    && !row.country;
}

/** Skip example / Required-Optional helper rows from downloaded template */
export function isCompanyTemplateHelperRow(row) {
  const name = safeString(row.name);
  if (/^(Required|Optional)(\s|$|\()/i.test(name)) {
    return true;
  }
  const { examples } = generateCompanyTemplateData();
  const exampleName = examples[0];
  const exampleLicense = examples[1];
  if (name === exampleName && row.tradeLicenseNumber === exampleLicense) {
    return true;
  }
  return false;
}

function resolveLookupCode(value, lookups = []) {
  const raw = safeString(value);
  if (!raw) return { code: '', label: '' };
  const upper = raw.toUpperCase();
  const byCode = lookups.find((item) => String(item.code).toUpperCase() === upper);
  if (byCode) return { code: byCode.code, label: byCode.label };
  const byLabel = lookups.find((item) => String(item.label).toLowerCase() === raw.toLowerCase());
  if (byLabel) return { code: byLabel.code, label: byLabel.label };
  return { code: raw, label: raw };
}

export function validateImportedCompanyRow(row, {
  companyTypes = [],
  countries = [],
  entityClassifications = [],
  isCompanySecretary = false,
  assignedCompanyIds = [],
} = {}) {
  const errors = [];

  if (!row.name) errors.push('Company Name is required');
  if (!row.tradeLicenseNumber) errors.push('Trade License Number is required');
  if (!row.structureType) {
    errors.push('Structure Type is required (Parent or Subsidiary)');
  } else if (!['Parent', 'Subsidiary'].includes(row.structureType)) {
    errors.push('Structure Type must be Parent or Subsidiary');
  }
  if (!row.governanceTypeCode) {
    errors.push('Company Classification is required');
  }
  if (!row.country) errors.push('Country is required');

  if (row.structureType === 'Subsidiary' && !row.parentCompanyName) {
    errors.push('Parent Company Name is required for subsidiaries');
  }
  if (row.structureType === 'Parent' && row.parentCompanyName) {
    errors.push('Parent Company Name must be empty for parent companies');
  }

  if (isCompanySecretary && row.structureType === 'Parent') {
    errors.push('Company Secretaries can only import subsidiary companies');
  }

  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
    errors.push('Invalid email format');
  }

  if (row.shareholdingPercentage !== '') {
    const pct = parseShareholdingPercent(row.shareholdingPercentage);
    if (pct === null || pct < 0 || pct > 100) {
      errors.push('Shareholding % must be between 0 and 100');
    }
  }

  if (row.foundedYear !== '') {
    const year = Number(row.foundedYear);
    if (!Number.isInteger(year) || year < 1800 || year > new Date().getFullYear()) {
      errors.push('Founded Year must be a valid year');
    }
  }

  if (row.employeeCount !== '') {
    const count = Number(row.employeeCount);
    if (!Number.isInteger(count) || count < 0) {
      errors.push('Employees must be a non-negative integer');
    }
  }

  const governance = resolveLookupCode(row.governanceTypeCode, companyTypes);
  if (row.governanceTypeCode && companyTypes.length && !companyTypes.some(
    (t) => String(t.code).toUpperCase() === String(governance.code).toUpperCase()
  )) {
    errors.push(`Unknown Company Classification "${row.governanceTypeCode}"`);
  }

  if (row.country && countries.length) {
    const countryMatch = countries.some(
      (c) => String(c.code).toUpperCase() === row.country.toUpperCase()
        || String(c.label).toLowerCase() === row.country.toLowerCase()
    );
    if (!countryMatch) {
      errors.push(`Unknown Country "${row.country}"`);
    }
  }

  if (row.entityClassification && entityClassifications.length) {
    const entityMatch = entityClassifications.some(
      (c) => String(c.code).toUpperCase() === row.entityClassification.toUpperCase()
        || String(c.label).toLowerCase() === row.entityClassification.toLowerCase()
    );
    if (!entityMatch) {
      errors.push(`Unknown Entity Classification "${row.entityClassification}"`);
    }
  }

  if (isCompanySecretary && row.parentCompanyId) {
    const parentId = Number(row.parentCompanyId);
    const allowed = (assignedCompanyIds || []).map(Number);
    if (!allowed.includes(parentId)) {
      errors.push('Parent company must be one of your assigned companies');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    governanceTypeCode: governance.code || row.governanceTypeCode,
    governanceTypeName: governance.label || row.governanceTypeCode,
  };
}

export function buildCompanyDescription({ address, city, poBox }) {
  return [address, city, poBox].filter(Boolean).join(', ');
}

export function buildCompanyCreatePayload(company) {
  return {
    company_name: company.name,
    registration_number: company.tradeLicenseNumber,
    country: company.country,
    description: buildCompanyDescription(company),
    parent_company_id: company.structureType === 'Subsidiary' && company.parentCompanyId
      ? Number(company.parentCompanyId)
      : null,
    shareholding_percentage: company.structureType === 'Subsidiary' && company.shareholdingPercentage !== ''
      ? parseShareholdingPercent(company.shareholdingPercentage)
      : null,
    company_type_code: company.governanceTypeCode,
    company_type_name: company.governanceTypeName || company.governanceTypeCode,
    phone: company.phone || null,
    email: company.email || null,
    website: company.website || null,
    capital: company.capital || null,
    founded_year: company.foundedYear !== '' ? Number(company.foundedYear) : null,
    employee_count: company.employeeCount !== '' ? Number(company.employeeCount) : null,
    ceo_name: company.ceoName || null,
    entity_classification: company.entityClassification || null,
  };
}

export function calculateCompanySimilarityScore(imported, existing) {
  let score = 0;
  let maxScore = 0;

  if (imported.tradeLicenseNumber && existing.tradeLicenseNumber) {
    maxScore += 50;
    if (imported.tradeLicenseNumber.toLowerCase() === existing.tradeLicenseNumber.toLowerCase()) {
      score += 50;
    }
  }

  if (imported.name && existing.name) {
    maxScore += 30;
    const a = imported.name.toLowerCase();
    const b = existing.name.toLowerCase();
    if (a === b) {
      score += 30;
    } else if (a.includes(b) || b.includes(a)) {
      score += 15;
    }
  }

  if (imported.country && existing.country) {
    maxScore += 10;
    if (imported.country.toLowerCase() === existing.country.toLowerCase()) {
      score += 10;
    }
  }

  if (imported.email && existing.email) {
    maxScore += 10;
    if (imported.email.toLowerCase() === existing.email.toLowerCase()) {
      score += 10;
    }
  }

  if (maxScore === 0) return 0;
  return Math.round((score / maxScore) * 100);
}

export function getMatchConfidenceLevel(score) {
  if (score >= 90) return { level: 'exact', label: 'Exact Match', color: 'error' };
  if (score >= 70) return { level: 'high', label: 'High Confidence', color: 'warning' };
  if (score >= 50) return { level: 'medium', label: 'Possible Match', color: 'info' };
  return { level: 'low', label: 'Low Confidence', color: 'default' };
}

export function getUserAssignedCompanyIds(user) {
  if (!user) return [];
  if (Array.isArray(user.assigned_company_ids) && user.assigned_company_ids.length) {
    return user.assigned_company_ids.map(Number);
  }
  if (user.assigned_company_id) {
    return [Number(user.assigned_company_id)];
  }
  return [];
}
